import { useEffect, useRef, useState } from 'react';
import { useEmVista } from './use-em-vista';

function prefereMovimentoReduzido(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Conta de 0 até `valorFinal` quando o elemento referenciado entra na
 * viewport (via `useEmVista`), uma vez só, com ease-out ao longo de
 * `duracaoMs`. Base do componente `<Numero>`.
 *
 * ## O estado inicial é o valor FINAL, nunca 0
 * O que o primeiro render devolve é o que o pré-render grava no HTML — e a
 * página chegou a ser servida dizendo "com 0 cláusulas versionadas na
 * biblioteca" para o Google, para leitor sem JS e para preview de link,
 * porque o estado nascia em 0 esperando uma animação que só roda no
 * navegador. O HTML carrega o número verdadeiro; a contagem é efeito de
 * cliente, disparada ao entrar na viewport.
 *
 * Sob `prefers-reduced-motion: reduce` — ou sem `IntersectionObserver` no
 * ambiente, caso em que `useEmVista` já resolve `emVista` como `true` de
 * cara — o valor final fica como está, nunca anima.
 */
export function useContador<T extends HTMLElement>(
  valorFinal: number,
  duracaoMs = 1200,
): { ref: (elemento: T | null) => void; valor: number } {
  const { ref, emVista } = useEmVista<T>(0.4);
  const [valor, setValor] = useState(valorFinal);
  const animadoRef = useRef(false);

  useEffect(() => {
    if (!emVista || animadoRef.current) return undefined;
    animadoRef.current = true;

    if (prefereMovimentoReduzido() || !('IntersectionObserver' in window)) {
      setValor(valorFinal);
      return undefined;
    }

    // A contagem parte do zero AGORA, não no primeiro render: é o que deixa
    // o HTML pré-renderizado com o número real e ainda preserva o gesto.
    setValor(0);

    const inicio = performance.now();
    let quadro: number;

    function passo(agora: number): void {
      const t = Math.min(1, (agora - inicio) / duracaoMs);
      const facilitado = 1 - Math.pow(1 - t, 3);
      setValor(Math.round(valorFinal * facilitado));
      if (t < 1) {
        quadro = requestAnimationFrame(passo);
      }
    }
    quadro = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(quadro);
  }, [emVista, valorFinal, duracaoMs]);

  return { ref, valor };
}
